/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Divider,
  Chip,
  TextField,
  InputAdornment,
  makeStyles,
} from "@material-ui/core";
import {
  DoneAll,
  Search,
  ExpandMore,
  ExpandLess,
  Favorite,
  ThumbUp,
  Lock,
} from "@material-ui/icons";
import { DeleteButton, EditButton } from "../../../components/ButttonsWithIcons";
import EditReservationModal from "./EditReservationModal";
import DeleteReservationModal from "./deleteReservationModal";
import CloseServiceModal from "./closeServiceModal";
import PrintDialog from "../../../components/PrintDialog";
import { getLocale, getTranslation } from "../../../heplers/translationHelper";
import { Post } from "../../../actions";
import { Post_ReservationStatus_URL } from "../../../constants/apiUrls";
import { useSnackbar } from "notistack";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import { format } from "../../../heplers/format";

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: "#f5f5f5",
    minHeight: "100vh",
    fontFamily: "'Helvetica Neue', Arial, sans-serif",
  },
  filterBar: {
    padding: "10px 12px 0",
    backgroundColor: "#f5f5f5",
    position: "sticky",
    top: 0,
    zIndex: 10,
  },
  toggleGroup: {
    width: "100%",
    backgroundColor: "#fff",
    borderRadius: 8,
    overflow: "hidden",
    border: "1px solid #e0e0e0",
  },
  toggleBtn: {
    flex: 1,
    fontSize: 12,
    fontWeight: 600,
    textTransform: "none",
    height: 36,
    border: "none !important",
    color: "#666",
    "&.Mui-selected": {
      backgroundColor: "#4caf50 !important",
      color: "#fff !important",
    },
  },
  searchBox: {
    margin: "8px 12px",
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      backgroundColor: "#fff",
      fontSize: 14,
    },
    "& .MuiOutlinedInput-input": {
      padding: "10px 14px",
    },
  },
  serviceBlock: {
    marginBottom: 4,
  },
  serviceHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 16px",
    backgroundColor: "#e8f5e9",
    borderLeft: "4px solid #4caf50",
    cursor: "pointer",
    userSelect: "none",
  },
  serviceTitle: {
    fontWeight: 700,
    fontSize: 15,
    color: "#2e7d32",
  },
  serviceCount: {
    fontWeight: 700,
    fontSize: 15,
    color: "#2e7d32",
  },
  timeSlot: {
    padding: "6px 16px 2px",
    backgroundColor: "#fafafa",
    borderBottom: "1px solid #f0f0f0",
  },
  timeLabel: {
    fontSize: 12,
    color: "#888",
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  timePill: {
    fontSize: 13,
    fontWeight: 700,
    color: "#555",
  },
  slotPersons: {
    fontSize: 12,
    color: "#aaa",
    marginLeft: 4,
  },
  reservationCard: {
    display: "flex",
    alignItems: "center",
    backgroundColor: "#fff",
    borderRadius: 8,
    marginBottom: 6,
    padding: "10px 12px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.07)",
    position: "relative",
    overflow: "hidden",
  },
  reservationCardCancelled: {
    opacity: 0.55,
    textDecoration: "line-through",
  },
  confirmedStripe: {
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    width: 3,
    backgroundColor: "#4caf50",
    borderRadius: "8px 0 0 8px",
  },
  reservationName: {
    fontSize: 14,
    fontWeight: 600,
    color: "#222",
    flex: 1,
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  reservationMeta: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginLeft: 8,
    flexShrink: 0,
  },
  personChip: {
    display: "flex",
    alignItems: "center",
    gap: 3,
    fontSize: 13,
    color: "#555",
    fontWeight: 600,
  },
  personIcon: {
    fontSize: 16,
    color: "#888",
  },
  tableChip: {
    fontSize: 13,
    color: "#555",
    fontWeight: 600,
    minWidth: 30,
    textAlign: "right",
  },
  lockedTable: {
    display: "flex",
    alignItems: "center",
    gap: 2,
    color: "#bbb",
    textDecoration: "line-through",
  },
  actionRow: {
    display: "flex",
    alignItems: "center",
    gap: 0,
    marginLeft: 4,
  },
  actionBtn: {
    padding: 4,
  },
  statusDone: {
    color: "#4caf50",
  },
  allergyChip: {
    fontSize: 10,
    height: 18,
    backgroundColor: "#fff3e0",
    color: "#e65100",
    marginLeft: 4,
    "& .MuiChip-label": { padding: "0 6px" },
  },
  emptyState: {
    padding: "40px 20px",
    textAlign: "center",
    color: "#aaa",
    fontSize: 14,
  },
}));

/**
 * Groups reservations by their service label (derived from reservationTime).
 * A "service" is Midi (morning/noon slots) or Soir (evening slots).
 * Adjust the cutoff hour to match your business logic.
 */
const groupReservationsByService = (reservations) => {
  const services = {};

  reservations.forEach((r) => {
    // Extract hour from reservationTime (supports "HH:MM" or "HH:MM:SS" or ISO)
    let hour = 0;
    if (r.reservationTime) {
      const timeStr = r.reservationTime;
      const parts = timeStr.includes("T")
        ? timeStr.split("T")[1]?.split(":")
        : timeStr.split(":");
      hour = parseInt(parts?.[0] || "0", 10);
    } else if (r.reservationDate) {
      hour = new Date(r.reservationDate).getHours();
    }

    // Determine service name — adjust cutoff as needed
    const serviceName = hour < 16 ? getTranslation("Morning") : getTranslation("Evening");

    // Derive display date
    const dateLabel = r.reservationDate
      ? new Date(r.reservationDate).toLocaleDateString(getLocale(), {
          weekday: "short",
          day: "numeric",
          month: "short",
        })
      : "";

    const serviceKey = `${serviceName}__${dateLabel}`;

    if (!services[serviceKey]) {
      services[serviceKey] = {
        serviceKey,
        serviceName,
        dateLabel,
        totalPersons: 0,
        timeSlots: {},
      };
    }

    services[serviceKey].totalPersons += (r.persons || 0) + (r.kids || 0);

    const timeKey = r.reservationTime
      ? r.reservationTime.substring(0, 5)
      : "--:--";

    if (!services[serviceKey].timeSlots[timeKey]) {
      services[serviceKey].timeSlots[timeKey] = {
        timeKey,
        slotPersons: 0,
        reservations: [],
      };
    }
    services[serviceKey].timeSlots[timeKey].slotPersons +=
      (r.persons || 0) + (r.kids || 0);
    services[serviceKey].timeSlots[timeKey].reservations.push(r);
  });

  return Object.values(services).map((s) => ({
    ...s,
    timeSlots: Object.values(s.timeSlots).sort((a, b) =>
      a.timeKey.localeCompare(b.timeKey)
    ),
  }));
};

const parseTableNames = (tableReserved) => {
  if (!tableReserved) return "";
  return tableReserved
    .split("|")
    .map((t) => t.split(",")[1])
    .filter(Boolean)
    .join(", ");
};

const ReservationMobileView = ({
  reservations = [],
  params,
  setParams,
  stats,
  loading,
  printData,
  refreshReservations,
  ref,
}) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const [selectedReservation, setSelectedReservation] = useState({
    persons: 0,
    reservationDate: Date.now(),
    purposeOfReservation: 0,
    timeSlotId: "",
    title: "",
    email: "",
    phone: "",
    subscribeForNews: false,
    haveSmallKids: false,
    kids: 0,
    acceptTerms: false,
    allergies: "",
  });
  const [isEdit, setIsEdit] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showReservationForm, setShowReservationForm] = useState(false);
  const [showCloseService, setShowCloseService] = useState(false);
  const [searchStr, setSearchStr] = useState("");
  const [collapsedServices, setCollapsedServices] = useState({});

  const handleEdit = (data) => {
    setIsEdit(true);
    setShowReservationForm(true);
    setSelectedReservation(data);
  };

  const confirmDelete = (data) => {
    setShowDeleteConfirm(true);
    setSelectedReservation(data);
  };

  const handleChangeStatus = (row) => {
    Post(
      [{ id: row?.id, status: 1 }],
      Post_ReservationStatus_URL,
      null,
      (res) => {
        enqueueSnackbar(
          res?.data
            ? getTranslation(res?.data, res?.data, res?.data)
            : getTranslation(
                "Status changed successfully",
                "Status changed successfully",
                "Status changed successfully"
              ),
          { variant: "success" }
        );
        if (refreshReservations) refreshReservations();
      },
      (err) => {
        enqueueSnackbar(
          err?.data
            ? err?.data
            : getTranslation(
                "Something went wrong. Please try Again",
                "Something went wrong. Please try Again",
                "Something went wrong. Please try Again"
              ),
          { variant: "error" }
        );
      }
    );
  };

  const toggleService = (key) => {
    setCollapsedServices((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Filter by search + status toggle
  const filteredReservations = reservations.filter((r) => {
    const matchSearch =
      !searchStr ||
      r.title?.toLowerCase().includes(searchStr.toLowerCase()) ||
      r.email?.toLowerCase().includes(searchStr.toLowerCase());

    const statusFilter = params?.status;
    const matchStatus =
      !statusFilter || statusFilter === "all"
        ? true
        : statusFilter === "confirmed"
        ? r.status === 1
        : statusFilter === "notconfirmed"
        ? r.status === 0
        : true;

    return matchSearch && matchStatus;
  });

  const grouped = groupReservationsByService(filteredReservations);

  return (
    <Box className={classes.root}>
      {/* Status Toggle Filter */}
      <Box className={classes.filterBar}>
        <ToggleButtonGroup
          exclusive
          value={params?.status}
          onChange={(e, value) => {
            if (value && setParams) setParams({ ...params, status: value });
          }}
          className={classes.toggleGroup}
        >
          <ToggleButton value="all" className={classes.toggleBtn}>
            {getTranslation("All", "All", "All")}
          </ToggleButton>
          <ToggleButton value="notconfirmed" className={classes.toggleBtn}>
            {getTranslation("Not Confirmed", "Not Confirmed", "Not Confirmed")}
          </ToggleButton>
          <ToggleButton value="confirmed" className={classes.toggleBtn}>
            {getTranslation("Confirmed", "Confirmed", "Confirmed")}
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Search */}
      <Box className={classes.searchBox}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder={getTranslation("Search...", "Search...", "Search...")}
          value={searchStr}
          onChange={(e) => setSearchStr(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search style={{ color: "#bbb", fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Grouped Service Blocks */}
      {grouped.length === 0 ? (
        <Box className={classes.emptyState}>
          <Typography>
            {getTranslation(
              "No reservations found",
              "No reservations found",
              "No reservations found"
            )}
          </Typography>
        </Box>
      ) : (
        grouped.map((service) => {
          const isCollapsed = collapsedServices[service.serviceKey];
          // Midi shows X / Y format (confirmed / total), Soir shows total
          const isMidi = service.serviceName === "Midi";
          const confirmedPersons = service.timeSlots
            .flatMap((ts) => ts.reservations)
            .filter((r) => r.status === 1)
            .reduce((sum, r) => sum + (r.persons || 0) + (r.kids || 0), 0);

          return (
            <Box key={service.serviceKey} className={classes.serviceBlock}>
              {/* Service Header */}
              <Box
                className={classes.serviceHeader}
                onClick={() => toggleService(service.serviceKey)}
              >
                <Typography className={classes.serviceTitle}>
                  {service.serviceName}{" "}
                  {service.dateLabel && (
                    <span style={{ fontWeight: 400, fontSize: 13 }}>
                      ({service.dateLabel})
                    </span>
                  )}
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography className={classes.serviceCount}>
                    {isMidi
                      ? `${confirmedPersons} / ${service.totalPersons} personnes`
                      : `${service.totalPersons} personnes`}
                  </Typography>
                  {isCollapsed ? (
                    <ExpandMore style={{ color: "#2e7d32", fontSize: 20 }} />
                  ) : (
                    <ExpandLess style={{ color: "#2e7d32", fontSize: 20 }} />
                  )}
                </Box>
              </Box>

              {/* Time Slots */}
              {!isCollapsed &&
                service.timeSlots.map((slot) => (
                  <Box key={slot.timeKey}>
                    {/* Time Label */}
                    <Box className={classes.timeSlot}>
                      <Typography className={classes.timeLabel}>
                        <span className={classes.timePill}>{slot.timeKey}</span>
                        <span className={classes.slotPersons}>
                          ({slot.slotPersons}{" "}
                          {getTranslation(
                            "personnes",
                            "personnes",
                            "personnes"
                          )}
                          )
                        </span>
                      </Typography>
                    </Box>

                    {/* Reservation Cards */}
                    <Box px={1} pb={1} pt={0.5} style={{ backgroundColor: "#fafafa" }}>
                      {slot.reservations.map((r) => {
                        const isCancelled = r.status === 2;
                        const isConfirmed = r.status === 1;
                        const tableNames = parseTableNames(r.tableReserved);
                        const isLocked = !r.tableReserved;

                        return (
                          <Box
                            key={r.id}
                            className={`${classes.reservationCard} ${
                              isCancelled ? classes.reservationCardCancelled : ""
                            }`}
                          >
                            {isConfirmed && (
                              <Box className={classes.confirmedStripe} />
                            )}

                            {/* Name */}
                            <Typography
                              className={classes.reservationName}
                              style={{ paddingLeft: isConfirmed ? 8 : 0 }}
                            >
                              {r.title}
                              {r.purposeOfReservation === 1 && (
                                <Favorite
                                  style={{ fontSize: 14, color: "#e91e63" }}
                                />
                              )}
                              {r.isVip && (
                                <ThumbUp
                                  style={{ fontSize: 14, color: "#1976d2" }}
                                />
                              )}
                              {r.allergies && r.allergies !== "N/A" && (
                                <Chip
                                  label="!"
                                  size="small"
                                  className={classes.allergyChip}
                                />
                              )}
                            </Typography>

                            {/* Meta: persons + table */}
                            <Box className={classes.reservationMeta}>
                              {/* Person count */}
                              <Box className={classes.personChip}>
                                <span
                                  style={{
                                    fontSize: 15,
                                    color: "#888",
                                  }}
                                >
                                  👤
                                </span>
                                <span>
                                  {isCancelled ? (
                                    <span
                                      style={{
                                        textDecoration: "line-through",
                                        color: "#bbb",
                                      }}
                                    >
                                      {r.persons}
                                    </span>
                                  ) : (
                                    r.persons
                                  )}
                                </span>
                              </Box>

                              {/* Table */}
                              <Box className={classes.tableChip}>
                                {isLocked ? (
                                  <Box className={classes.lockedTable}>
                                    <Lock style={{ fontSize: 13 }} />
                                    <span>{r.persons}</span>
                                  </Box>
                                ) : (
                                  <span>{tableNames}</span>
                                )}
                              </Box>
                            </Box>

                            {/* Actions */}
                            <Box className={classes.actionRow}>
                              {r.status === 0 && (
                                <IconButton
                                  className={classes.actionBtn}
                                  onClick={() => handleChangeStatus(r)}
                                  size="small"
                                >
                                  <DoneAll
                                    style={{ fontSize: 18, color: "#9e9e9e" }}
                                  />
                                </IconButton>
                              )}
                              <IconButton
                                className={classes.actionBtn}
                                onClick={() => handleEdit(r)}
                                size="small"
                              >
                                <span
                                  style={{ fontSize: 16, color: "#9e9e9e" }}
                                >
                                  ✏️
                                </span>
                              </IconButton>
                              <IconButton
                                className={classes.actionBtn}
                                onClick={() => confirmDelete(r)}
                                size="small"
                              >
                                <span
                                  style={{ fontSize: 16, color: "#e57373" }}
                                >
                                  🗑️
                                </span>
                              </IconButton>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                ))}
            </Box>
          );
        })
      )}

      {/* Modals — identical to desktop, props unchanged */}
      <EditReservationModal
        isEdit={isEdit}
        openModal={showReservationForm}
        onClose={() => {
          setSelectedReservation(null);
          setShowReservationForm(false);
        }}
        selectedReservation={selectedReservation}
        params={params}
        setParams={setParams}
        refreshReservations={() => refreshReservations && refreshReservations()}
      />
      <DeleteReservationModal
        openModal={showDeleteConfirm}
        onClose={setShowDeleteConfirm}
        selectedReservation={selectedReservation}
        params={params}
        refreshReservations={refreshReservations}
        setParams={setParams}
      />
      <CloseServiceModal
        openDialog={showCloseService}
        handleClose={setShowCloseService}
      />
      <div style={{ display: "none" }}>
        <PrintDialog ref={ref} params={params} rows={printData} />
      </div>
    </Box>
  );
};

export default ReservationMobileView;